import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
// import { connect } from 'react-redux';
import AttachmentsGallery from '@components/attachments-gallery';
import ChatTabs from '@components/chat-tabs';
import Notes from '@components/notes';
import SearchInput from '@components/search-input';
import { Avatar, Typography } from '@components/ui';
import useEnvService from '@hooks/app/useEnvService';
import useIsCompact from '@hooks/app/useIsCompact';
import useClientDetail from '@hooks/query/clients/useClientDetail';
import getFullNameFromProfile from '@lib/utils/names/getFullNameFromProfile';
import MobileBar from '@pages/life-id/components/mobile-bar';
import Programs from '@pages/life-id/components/programs';
import { NoConnectionSC } from '@pages/life-id/life-id.styled';
import { actions as chatActions } from '@store/ducks/direct-chat';
import { actions as userActions } from '@store/ducks/users';
import { RouteComponentProps } from 'react-router-dom';
import { TSearchParams } from 'types/users';
import { ChatDirectWindow } from '../../components/chat-window';
import TStore from '../../types/store';
import { ChatRowSC, TopBarSC } from './chat.styled';

type TProps = {} & RouteComponentProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const Chat: React.FC<TProps> = ({
  match,
  connectionStatus,
  getMessagesRequest,
  providerId,
  setReceiver,
  postSearchRequest,
  resetMessages,
  messagesLoading,
}) => {
  const params: any = match.params;
  const { userId } = params;
  const [activeTab, setActiveTab] = React.useState('CHAT');
  const [searchQuery, setSearchQuery] = React.useState<string>('');

  const [env] = useEnvService();
  const isCompact = useIsCompact();
  const isHilbiChat = env.xmpp.hilbiId === userId;

  const { data } = useClientDetail(userId, { enabled: !isHilbiChat });
  const jabberId = isHilbiChat ? env.xmpp.hilbiJid : data?.jabberId;

  React.useEffect(() => {
    if (connectionStatus === 'CONNECTED' && jabberId) {
      getMessagesRequest(jabberId);
      setReceiver(jabberId);
      postSearchRequest(providerId, { jabberIds: [jabberId] });
    }
  }, [connectionStatus, providerId, jabberId]);

  React.useEffect(() => {
    return () => {
      resetMessages();
    };
  }, [resetMessages]);

  const onChatSearch = (searchQuery: string) => {
    setSearchQuery(searchQuery);
  };

  return (
    <>
      {isCompact ? (
        <MobileBar userId={userId} activeKey="communication" />
      ) : (
        <TopBarSC>
          <Avatar src={data?.profile.profileImage} />
          <div style={{ marginLeft: 16, display: 'block' }}>
            <Typography as={'div'}>
              {isHilbiChat ? 'Hilbi' : getFullNameFromProfile(data?.profile)}
            </Typography>
          </div>
        </TopBarSC>
      )}
      <ChatRowSC>
        <div className="col-chat">
          <div style={{ padding: 16 }}>
            <SearchInput isBlock onSearch={onChatSearch} />
            <ChatTabs
              selected={activeTab}
              onChange={setActiveTab}
              isVideo={false}
              style={{ padding: 0, marginTop: 8 }}
            />
          </div>
          {activeTab === 'CHAT' ? (
            connectionStatus === 'FAILED' ? (
              <NoConnectionSC>
                <p>
                  <FormattedMessage id="chat.no_connection_title" />
                </p>
              </NoConnectionSC>
            ) : (
              <ChatDirectWindow searchQuery={searchQuery} />
            )
          ) : activeTab === 'NOTES' ? (
            <Notes userId={userId} />
          ) : activeTab === 'TOOLS' ? (
            <Programs userId={userId} />
          ) : connectionStatus === 'FAILED' ? (
            <NoConnectionSC>
              <p>
                <FormattedMessage id="chat.no_connection_title" />
              </p>
            </NoConnectionSC>
          ) : (
            <AttachmentsGallery type="DIRECT_CHAT" messagesLoading={messagesLoading} />
          )}
        </div>
      </ChatRowSC>
    </>
  );
};

const mapStateToProps = (store: TStore) => ({
  connectionStatus: store.xmpp.status,
  loading: store.rooms.loading,
  providerId: store.users.currentProvider?.id!,
  messagesLoading: store.directChat.isLoading,
});

const mapDispatchToProps = (dispatch: any) => ({
  getMessagesRequest: (from: string) => dispatch(chatActions.getMessagesRequest(from)),
  setReceiver: (receiver: string) => dispatch(chatActions.setReceiver(receiver)),
  postSearchRequest: (providerId: string, query: TSearchParams) =>
    dispatch(userActions.postSearchRequest({ providerId, ...query })),
  resetMessages: () => dispatch(chatActions.resetMessages()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Chat);
